import { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Select, Space, Statistic, Table, Tag } from 'antd';
import { UsergroupAddOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Paragraph from 'antd/lib/typography/Paragraph';
import Title from 'antd/lib/typography/Title';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import LeftMenuSettings from './LeftSettings';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import { TextService } from '../../Services/TextService';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import {
    WebApiIntegrationsDtoMemberDataMismatchDto,
    WebApiIntegrationsDtoMemberStatusMismatchDto,
    WebApiIntegrationsDtoMemberSyncReportDto,
    WebApiIntegrationsDtoMemberSyncResultDto,
    WebApiIntegrationsDtoMissingMemberDto,
} from '../../Repository/eventbookyapi';

const mapMemberStatus = (status: number | undefined, GetText: (key: string) => string): string => {
    switch (status) {
        case 0:
            return GetText('MemberList_StatusActive');
        case 1:
            return GetText('MemberList_StatusCancelled');
        case 2:
            return GetText('MemberList_StatusUnconfirmedEmail');
        case 3:
            return GetText('MemberList_StatusUnconfirmedInitialPayment');
        default:
            return '-';
    }
};

const textOrDash = (value?: string | null) => {
    return value && value.length > 0 ? value : '-';
};

const FortnoxMismatchReport = () => {
    const GetText = TextService.GetText;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null as WebApiIntegrationsDtoMemberSyncReportDto | null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedMismatchFields, setSelectedMismatchFields] = useState([] as string[]);
    const [syncError, setSyncError] = useState('');
    const [creatingIds, setCreatingIds] = useState(new Set<number>());
    const [createdIds, setCreatedIds] = useState(new Set<number>());
    const [creatingAll, setCreatingAll] = useState(false);

    // Get integration type from query parameter, default to Fortnox
    const integrationType = searchParams.get('integration') === 'spiris' ? IntegrationType.Spiris : IntegrationType.Fortnox;
    const isSpirls = integrationType === IntegrationType.Spiris;
    const integrationName = isSpirls ? 'Spiris' : 'Fortnox';

    const helpContent_se = <>
        <Paragraph>Rapporten jämför medlemsdata mellan OneFront och {integrationName} och visar vad som skiljer sig åt.</Paragraph>
        <Paragraph><b>Kopplingslogik:</b> Systemen länkas samman via <b>medlemsnummer i OneFront</b> och <b>kundnummer i {integrationName}</b>. En medlem utan matchande kundnummer visas som saknad i integrationen.</Paragraph>
        <Paragraph><b>Automatisk synkronisering:</b> Synken körs automatiskt en gång per dag.</Paragraph>
        <Paragraph><b>De tre sektionerna:</b></Paragraph>
        <ul>
            <li><b>Finns i OneFront – saknas i {integrationName}</b> – Medlemmar som finns i OneFront men inte har ett matchande kundnummer i {integrationName}. Dessa kan skapas direkt i {integrationName} via knappen på varje rad.</li>
            <li><b>Dataavvikelser</b> – Medlemmen finns i båda systemen men fältvärden som namn, e-post eller adress stämmer inte överens.</li>
            <li><b>Statusavvikelser</b> – Medlemmen finns i båda systemen men aktiv/inaktiv-statusen skiljer sig åt.</li>
        </ul>
    </>;

    const helpContent_en = <>
        <Paragraph>The report compares member data between OneFront and {integrationName} and shows what differs.</Paragraph>
        <Paragraph><b>Matching logic:</b> The systems are linked via <b>member number in OneFront</b> and <b>customer number in {integrationName}</b>. A member without a matching customer number appears as missing in the integration.</Paragraph>
        <Paragraph><b>Automatic sync:</b> Sync runs automatically once per day.</Paragraph>
        <Paragraph><b>The three sections:</b></Paragraph>
        <ul>
            <li><b>Exists in OneFront – missing in {integrationName}</b> – Members that exist in OneFront but have no matching customer number in {integrationName}. These can be created directly in {integrationName} via the button on each row.</li>
            <li><b>Data mismatches</b> – The member exists in both systems but field values such as name, email or address do not match.</li>
            <li><b>Status mismatches</b> – The member exists in both systems but the active/inactive status differs.</li>
        </ul>
    </>;

    const loadReport = async () => {
        setLoading(true);
        setError('');

        try {
            const result = await IntegrationService.GetMemberSyncReport(integrationType);
            setReport(result);
        } catch {
            setError(GetText('Integrations_MismatchReportLoadError'));
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    const createMember = async (memberId: number) => {
        setCreatingIds(prev => new Set(prev).add(memberId));
        try {
            await IntegrationService.CreateMemberInIntegration(memberId, integrationType);
            setCreatedIds(prev => new Set(prev).add(memberId));
        } catch (e: any) {
            const msg = e?.message ?? GetText('Integrations_CreateMemberInFortnox_Error').replace('Fortnox', integrationName);
            setSyncError(msg);
        } finally {
            setCreatingIds(prev => { const s = new Set(prev); s.delete(memberId); return s; });
        }
    };

    const createAllMissing = async () => {
        setCreatingAll(true);
        setSyncError('');
        try {
            const batchJobId = await IntegrationService.CreateMissingFortnoxCustomers();
            if (batchJobId) {
                navigate(`/${getCurrentLanguage()}/batchJobStatus/${batchJobId}`);
            } else {
                await loadReport();
            }
        } catch (e: any) {
            setSyncError(e?.message ?? GetText('Integrations_CreateAllInFortnox_Error'));
        } finally {
            setCreatingAll(false);
        }
    };

    useEffect(() => {
        loadReport();
    }, [integrationType]);

    const missingColumns: ColumnsType<WebApiIntegrationsDtoMissingMemberDto> = [
        {
            title: GetText('Integrations_MismatchReport_ColumnUniqueId'),
            dataIndex: 'uniqueId',
            key: 'uniqueId',
            width: 140,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnName'),
            dataIndex: 'fullName',
            key: 'fullName',
            render: (_: any, record: WebApiIntegrationsDtoMissingMemberDto) => (
                <a href={`/${getCurrentLanguage()}/users/${record.memberId}`} target="_blank" rel="noreferrer">
                    {record.fullName ?? '-'}
                </a>
            ),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnEmail'),
            dataIndex: 'email',
            key: 'email',
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnPhone'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnStatus'),
            dataIndex: 'status',
            key: 'status',
            width: 180,
            render: (value: number | undefined) => mapMemberStatus(value, GetText),
        },
        {
            key: 'actions',
            width: 160,
            render: (_: any, record: WebApiIntegrationsDtoMissingMemberDto) => {
                const id = record.memberId!;
                if (createdIds.has(id)) {
                    return <Tag color="green">{GetText('Integrations_CreateMemberInFortnox_Success')}</Tag>;
                }
                return (
                    <BookyButton
                        text={GetText('Integrations_CreateMemberInFortnox').replace('Fortnox', integrationName)}
                        loading={creatingIds.has(id)}
                        disabled={creatingIds.has(id)}
                        onClick={() => createMember(id)}
                    />
                );
            },
        },
    ];

    const dataMismatchColumns: ColumnsType<WebApiIntegrationsDtoMemberDataMismatchDto> = [
        {
            title: GetText('Integrations_MismatchReport_ColumnMemberId'),
            dataIndex: 'memberId',
            key: 'memberId',
            width: 100,
            render: (value: number) => <a href={`/${getCurrentLanguage()}/users/${value}`} target="_blank" rel="noreferrer">{value}</a>,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnCustomerNumber'),
            dataIndex: 'customerNumber',
            key: 'customerNumber',
            width: 130,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnName'),
            dataIndex: 'fullName',
            key: 'fullName',
            width: 220,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnMismatchedFields'),
            dataIndex: 'mismatchedFields',
            key: 'mismatchedFields',
            render: (value: Array<string> | null | undefined) => (
                <Space wrap>
                    {(value ?? []).map((field) => (
                        <Tag key={field} color="orange">{field}</Tag>
                    ))}
                    {!value || value.length === 0 ? '-' : ''}
                </Space>
            ),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnValues'),
            key: 'values',
            render: (_, record) => (
                <div>
                    <div>{GetText('Integrations_MismatchReport_ValueName')}: {textOrDash(record.oneFrontName)} / {textOrDash(record.externalName)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueEmail')}: {textOrDash(record.oneFrontEmail)} / {textOrDash(record.externalEmail)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValuePhone')}: {textOrDash(record.oneFrontPhone)} / {textOrDash(record.externalPhone)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueAddress1')}: {textOrDash(record.oneFrontAddress1)} / {textOrDash(record.externalAddress1)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueAddress2')}: {textOrDash(record.oneFrontAddress2)} / {textOrDash(record.externalAddress2)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueCity')}: {textOrDash(record.oneFrontCity)} / {textOrDash(record.externalCity)}</div>
                    <div>{GetText('Integrations_MismatchReport_ValueZip')}: {textOrDash(record.oneFrontZipCode)} / {textOrDash(record.externalZipCode)}</div>
                    {report?.syncPersonalNumber && (
                        <div>{GetText('Integrations_MismatchReport_ValuePersonalNumber')}: {textOrDash(record.oneFrontPersonalNumber)} / {textOrDash(record.externalPersonalNumber)}</div>
                    )}
                    {report?.syncExtraToNotes != null && (
                        <div>{GetText('Integrations_MismatchReport_ValueNotes')}: {textOrDash(record.oneFrontNotes)} / {textOrDash(record.externalNotes)}</div>
                    )}
                </div>
            ),
        },
    ];

    const statusMismatchColumns: ColumnsType<WebApiIntegrationsDtoMemberStatusMismatchDto> = [
        {
            title: GetText('Integrations_MismatchReport_ColumnCustomerNumber'),
            dataIndex: 'customerNumber',
            key: 'customerNumber',
            width: 130,
            render: (value: string | null | undefined) => textOrDash(value),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnName'),
            dataIndex: 'fullName',
            key: 'fullName',
            width: 220,
            render: (_: any, record: WebApiIntegrationsDtoMemberStatusMismatchDto) => (
                <a href={`/${getCurrentLanguage()}/users/${record.memberId}`} target="_blank" rel="noreferrer">
                    {record.fullName ?? '-'}
                </a>
            ),
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnOneFrontActive'),
            dataIndex: 'oneFrontIsActive',
            key: 'oneFrontIsActive',
            width: 140,
            render: (value: boolean | undefined) => value ? <Tag color="green">{GetText('Common_Yes')}</Tag> : <Tag color="red">{GetText('Common_No')}</Tag>,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnExternalActive'),
            dataIndex: 'externalIsActive',
            key: 'externalIsActive',
            width: 140,
            render: (value: boolean | undefined) => value ? <Tag color="green">{GetText('Common_Yes')}</Tag> : <Tag color="red">{GetText('Common_No')}</Tag>,
        },
        {
            title: GetText('Integrations_MismatchReport_ColumnOneFrontStatus'),
            dataIndex: 'oneFrontStatus',
            key: 'oneFrontStatus',
            width: 200,
            render: (value: number | undefined) => mapMemberStatus(value, GetText),
        },
    ];

    const mismatchCount = useMemo(() => {
        if (!report) {
            return 0;
        }

        return (report.missingInIntegration?.length ?? 0)
            + (report.dataMismatches?.length ?? 0)
            + (report.statusMismatches?.length ?? 0);
    }, [report]);

    const mismatchFieldOptions = useMemo(() => {
        const fields = new Set<string>();

        (report?.dataMismatches ?? []).forEach((row) => {
            (row.mismatchedFields ?? []).forEach((field) => {
                if (field) {
                    fields.add(field);
                }
            });
        });

        return Array.from(fields)
            .sort((a, b) => a.localeCompare(b))
            .map((field) => ({ label: field, value: field }));
    }, [report]);

    const filteredDataMismatches = useMemo(() => {
        const rows = report?.dataMismatches ?? [];

        if (selectedMismatchFields.length === 0) {
            return rows;
        }

        return rows.filter((row) =>
            (row.mismatchedFields ?? []).some((field) => selectedMismatchFields.includes(field))
        );
    }, [report, selectedMismatchFields]);

    return <LayoutLeftMenu title={`${integrationName} ${GetText('Integrations_MismatchReportTitle').replace('Fortnox', '').trim()}`} pageHelpContent={getCurrentLanguage() === 'se' ? helpContent_se : helpContent_en} menu={<LeftMenuSettings selected={'integrations'} />}>
        <Paragraph>{GetText('Integrations_MismatchReportDescription').replace('Fortnox', integrationName)}</Paragraph>

        {syncError && <Alert style={{ marginBottom: 12 }} type="error" message={syncError} showIcon />}

        {error ? <Alert style={{ marginTop: 12 }} type="error" message={error} showIcon /> : null}

        <Row gutter={16} style={{ marginTop: 20, marginBottom: 20 }}>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic title={GetText('Integrations_MismatchReport_MembersInOneFront')} value={report?.totalMembers ?? 0} loading={loading} />
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic title={`${GetText('Integrations_MismatchReport_MembersInFortnox').replace('Fortnox', integrationName)}`} value={report?.totalExternalMembers ?? 0} loading={loading} />
                </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
                <Card>
                    <Statistic title={GetText('Integrations_MismatchReport_TotalMismatches')} value={mismatchCount} loading={loading} />
                </Card>
            </Col>
        </Row>

        {report && mismatchCount === 0 ? <Alert type="success" showIcon message={GetText('Integrations_MismatchReportNoMismatches')} /> : null}

        <Title level={4} style={{ marginTop: 20 }}>{GetText('Integrations_MismatchReport_SectionMissingInIntegration').replace('Fortnox', integrationName)}</Title>
        {!isSpirls && (report?.missingInIntegration?.length ?? 0) > 0 && (
            <Space direction="vertical" style={{ marginBottom: 12 }}>
                <BookyButton
                    icon={<UsergroupAddOutlined />}
                    text={GetText('Integrations_CreateAllInFortnox').replace('{0}', (report?.missingInIntegration?.length ?? 0).toString())}
                    loading={creatingAll}
                    disabled={creatingAll}
                    onClick={createAllMissing}
                />
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>{GetText('Integrations_CreateAllInFortnox_Hint')}</Paragraph>
            </Space>
        )}
        <Table
            rowKey={(record) => `missing-${record.memberId}-${record.uniqueId}`}
            columns={missingColumns}
            dataSource={report?.missingInIntegration ?? []}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '-' }}
            scroll={{ x: 900 }}
        />

        <Title level={4} style={{ marginTop: 20 }}>{GetText('Integrations_MismatchReport_SectionDataMismatches')}</Title>
        <Space wrap style={{ marginBottom: 12 }}>
            <Paragraph style={{ marginBottom: 0 }}>{GetText('Integrations_MismatchReport_FilterByField')}</Paragraph>
            <Select
                mode="multiple"
                allowClear
                style={{ minWidth: 320 }}
                placeholder={GetText('Integrations_MismatchReport_FilterByFieldPlaceholder')}
                options={mismatchFieldOptions}
                value={selectedMismatchFields}
                onChange={(values) => setSelectedMismatchFields(values)}
            />
        </Space>
        <Table
            rowKey={(record) => `data-${record.memberId}-${record.customerNumber}`}
            columns={dataMismatchColumns}
            dataSource={filteredDataMismatches}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '-' }}
            scroll={{ x: 1400 }}
        />

        <Title level={4} style={{ marginTop: 20 }}>{GetText('Integrations_MismatchReport_SectionStatusMismatches')}</Title>
        <Table
            rowKey={(record) => `status-${record.memberId}-${record.customerNumber}`}
            columns={statusMismatchColumns}
            dataSource={report?.statusMismatches ?? []}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '-' }}
            scroll={{ x: 1000 }}
        />
    </LayoutLeftMenu>;
};

export default FortnoxMismatchReport;
